import { useContext, useRef, useState } from 'react';
import { AppState } from '../../context/general';
import { getApiHost } from '../../connector/auth';
import { nonNull } from 't-lib-ui/helpers';
import { TextButton, Input, DropDownSelector, DropDownSelectorValue } from 't-lib-ui/widgets';
import { getKeyHandler } from 't-lib-ui/structure';
import { usePutAdminFeed, uploadCustomImage, upscaleCustomImage, customImageFromUrl } from '../../connector/api';
import { getDefaultIconUrl } from '../../schema-copy/default-icon';
import { RestAdminFeed, FeedImageExt, ImageDefault, SquareFit } from '../../schema-copy/api-schema';
import { sparklesIcon, cloudUploadIcon, linkIcon } from '../icons';
import './FeedImageEditor.css';

interface FeedImageEditorProps {
  feed: RestAdminFeed;
  magic: string;
  /** Show the "create custom image" controls (AI upscale + upload). Catalog-editing pages only. */
  allowCustomCreation?: boolean;
  onUpdated: (feed: RestAdminFeed) => void;
}

type Kind = 'favicon' | 'feedimage' | 'custom';

const fitOptions: DropDownSelectorValue[] = [
  { id: 'crop', text: 'Crop to square (cut edges)', searchTextLc: 'crop to square' },
  { id: 'pad', text: 'Pad to square (white bars)', searchTextLc: 'pad to square' },
];

/**
 * Lets an admin pick a feed's default image (favicon / feed image / custom / fallback) and — when
 * allowCustomCreation — create the custom image by AI-upscaling a source, uploading a logo, or
 * downloading one from a URL (e.g. a logo found on the feed's site).
 * Previews come from the magic-gated admin image endpoint (custom/favicon/feedimage) or the
 * generated fallback icon. Selecting an option persists imageDefault via PUT /api/admin/feed/:id.
 */
export default function FeedImageEditor({ feed, magic, allowCustomCreation, onUpdated }: FeedImageEditorProps) {
  const appState = useContext(AppState);
  const auth = nonNull(appState.state.authData);
  const putAdminFeed = usePutAdminFeed(feed.id, magic);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [settingDefault, setSettingDefault] = useState<ImageDefault | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [loadingUrl, setLoadingUrl] = useState(false);
  // How uploaded / downloaded non-square images are squared.
  const [fit, setFit] = useState<SquareFit>('crop');
  const [upscalingKind, setUpscalingKind] = useState<Kind | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Magic-gated admin image URL — usable directly as <img src> and as the AI-upscale source URL.
  const imageUrl = (kind: Kind, res: string, format?: string): string => {
    const params = new URLSearchParams({ magic, kind, res, ...(res == 'original' && format ? { format } : {}) });
    return `${getApiHost(auth)}/api/admin/feed/${feed.id}/image?${params.toString()}`;
  };

  // Previews are shown at the home page's 128px tile size, so use the 192px variant to stay sharp.
  const previewUrl = (kind: Kind, ext: FeedImageExt): string =>
    ext == 'svg' ? imageUrl(kind, 'original', 'svg') : imageUrl(kind, 'w192');

  interface Option { id: ImageDefault; label: string; src: string; sourceExt?: FeedImageExt; sourceKind?: Kind; }
  const options: Option[] = [];
  if (feed.faviconExt) options.push({ id: 'favicon', label: 'Favicon', src: previewUrl('favicon', feed.faviconExt), sourceExt: feed.faviconExt, sourceKind: 'favicon' });
  if (feed.feedImageExt) options.push({ id: 'feedImage', label: 'Feed image', src: previewUrl('feedimage', feed.feedImageExt), sourceExt: feed.feedImageExt, sourceKind: 'feedimage' });
  if (feed.customImageExt) options.push({ id: 'custom', label: 'Custom', src: previewUrl('custom', feed.customImageExt) });
  options.push({ id: 'fallback', label: 'Fallback', src: getDefaultIconUrl(feed.shortTitle || feed.title || '') });

  const handleSelect = async (id: ImageDefault) => {
    if (settingDefault || feed.imageDefault == id) return;
    setErrorMsg(null);
    setSettingDefault(id);
    try {
      onUpdated(await putAdminFeed({ imageDefault: id }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to set default image');
    } finally {
      setSettingDefault(null);
    }
  };

  const handleUpscale = async (kind: Kind, ext: FeedImageExt) => {
    if (upscalingKind) return;
    setErrorMsg(null);
    setUpscalingKind(kind);
    try {
      onUpdated(await upscaleCustomImage(auth, feed.id, { url: imageUrl(kind, 'original', ext) }));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'AI upscale failed');
    } finally {
      setUpscalingKind(null);
    }
  };

  const handleUpload = async (file: File | undefined) => {
    if (!file || uploading) return;
    setErrorMsg(null);
    setUploading(true);
    try {
      onUpdated(await uploadCustomImage(auth, feed.id, file, fit));
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUseUrl = async () => {
    const url = imageUrlInput.trim();
    if (!url || loadingUrl) return;
    setErrorMsg(null);
    setLoadingUrl(true);
    try {
      onUpdated(await customImageFromUrl(auth, feed.id, { url, fit }));
      setImageUrlInput('');
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Loading the image failed');
    } finally {
      setLoadingUrl(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        {options.map((o, index) => {
          const selected = feed.imageDefault == o.id;
          // First tile joins the vertical flow (up/down); the rest are reached sideways (left/right),
          // mirroring the TabButtons pattern so a whole row counts as one flow stop.
          const focusClass = `flowFocussable${index > 0 ? ' sidewayFocus' : ''}`;
          return (
            <div
              key={o.id}
              onClick={() => handleSelect(o.id)}
              className={`feedImageTile ${focusClass}`}
              tabIndex={0}
              onKeyDown={getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', sidewaysBehaviour: 'both', onAction: () => handleSelect(o.id) })}
              title={`Use ${o.label} as the default image`}
              style={{
                textAlign: 'center',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '6px',
                border: `2px solid ${selected ? 'var(--success-color)' : 'var(--gray-850)'}`,
                background: selected ? '#eafaf0' : 'var(--surface-raised)',
                opacity: settingDefault && settingDefault != o.id ? 0.6 : 1,
              }}
            >
              <img
                src={o.src}
                alt={o.label}
                // Same size and shape as the home page's feed tiles (DiscoverTile).
                style={{ display: 'block', width: '128px', height: '128px', objectFit: 'cover', background: '#fff', borderRadius: feed.circularIcon ? '50%' : '6px' }}
              />
              <div style={{ marginTop: '6px', fontSize: '0.8rem', fontWeight: selected ? 700 : 500, color: selected ? 'var(--success-color)' : 'var(--gray-350)' }}>
                {o.label}{selected && ' ✓'}
              </div>
              {allowCustomCreation && o.sourceKind && o.sourceExt && (
                <div style={{ marginTop: '6px' }}>
                  <TextButton
                    label="AI → custom"
                    icon={sparklesIcon}
                    onClick={(e?: any) => { e?.stopPropagation?.(); handleUpscale(o.sourceKind!, o.sourceExt!); }}
                    focussable="sideway"
                    type="minor"
                    size="small"
                    busy={upscalingKind == o.sourceKind}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allowCustomCreation && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Non-square images:</span>
          <DropDownSelector
            selections={fitOptions}
            value={fit}
            onSelect={v => setFit(v.id as SquareFit)}
            focussable="flow"
          />
        </div>
      )}

      {allowCustomCreation && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <TextButton
            label="Upload logo"
            icon={cloudUploadIcon}
            onClick={() => fileInputRef.current?.click()}
            focussable="flow"
            type="minor"
            size="small"
            busy={uploading}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg"
            style={{ display: 'none' }}
            onChange={(e) => handleUpload(e.target.files?.[0])}
          />
          <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>PNG, JPG, WEBP, GIF or SVG · used as the custom image</span>
        </div>
      )}

      {allowCustomCreation && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Input
              value={imageUrlInput}
              onInput={setImageUrlInput}
              onEnter={handleUseUrl}
              placeholder="…or paste an image URL, e.g. a logo from the site"
              layout="singleCol"
            />
          </div>
          <TextButton
            label="Use URL"
            icon={linkIcon}
            onClick={handleUseUrl}
            focussable="sideway"
            type="minor"
            size="small"
            busy={loadingUrl}
            enabled={imageUrlInput.trim() ? 'enabled' : 'disabled'}
          />
        </div>
      )}

      {errorMsg && <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--error-color)' }}>{errorMsg}</div>}
    </div>
  );
}
